import React, { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { Screen } from '../shared/Screen';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { colors, fonts, spacing } from '../theme';

export function SignUpScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signUp, signInWithGoogle, loading } = useAuth();

  const handleSubmit = async () => {
    setErrorMessage(null);
    if (!email || !password) {
      const msg = t('auth.signUp.requiredFieldsMessage');
      setErrorMessage(msg);
      Alert.alert(t('auth.signUp.requiredFieldsTitle'), msg);
      return;
    }
    if (password.length < 8) {
      const msg = t('auth.signUp.shortPasswordMessage');
      setErrorMessage(msg);
      Alert.alert(t('auth.signUp.shortPasswordTitle'), msg);
      return;
    }
    try {
      await signUp(email, password);
    } catch (error) {
      const msg = (error as Error).message;
      setErrorMessage(msg);
      Alert.alert(t('auth.signUp.errorTitle', { defaultValue: 'Erro ao cadastrar' }), msg);
    }
  };

  const handleGoogleSignUp = async () => {
    setErrorMessage(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      const msg = (error as Error).message;
      setErrorMessage(msg);
      Alert.alert(t('auth.signUp.googleErrorTitle', { defaultValue: 'Erro ao cadastrar com Google' }), msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Screen>
      <Heading>{t('auth.signUp.title')}</Heading>
      <Button
        variant="line"
        title={t('auth.signUp.googleButton')}
        onPress={handleGoogleSignUp}
        loading={googleLoading}
        disabled={loading || googleLoading}
      />
      <View style={styles.dividerContainer}>
        <View style={styles.dividerLine} />
        <Text style={styles.dividerText}>{t('auth.signUp.divider')}</Text>
        <View style={styles.dividerLine} />
      </View>
      <TextField
        placeholder={t('auth.signUp.emailPlaceholder')}
        value={email}
        onChangeText={(text) => {
          setEmail(text);
          setErrorMessage(null);
        }}
        autoCapitalize="none"
      />
      <TextField
        placeholder={t('auth.signUp.passwordPlaceholder')}
        value={password}
        onChangeText={(text) => {
          setPassword(text);
          setErrorMessage(null);
        }}
        secureTextEntry
      />
      {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}
      {loading ? (
        <ActivityIndicator />
      ) : (
        <Button title={t('auth.signUp.submit')} onPress={handleSubmit} disabled={googleLoading} />
      )}
      <Button
        variant="ghost"
        title={t('auth.signUp.alreadyHaveAccount')}
        onPress={() => router.push('/(auth)/signin')}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  errorText: {
    fontFamily: fonts.body,
    fontSize: 14,
    color: colors.clay,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.sm,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.line,
  },
  dividerText: {
    marginHorizontal: spacing.sm,
    fontFamily: fonts.bodyMedium,
    fontSize: 13,
    color: colors.thread,
  },
});
